import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TokenService } from './token.service';
import { IndexedDBService } from './indexed-db.service';
import { EnrollUserService } from './enroll-user.service';
import { environment } from '@env/environment';
import { I18nService } from './i18n.service';

export interface UserToLogin {
  username: string;
  fuente: string;
  password: string;
  token: string
}

@Injectable({
  providedIn: 'root'
})
export class QueryParamHandlerService {

  private enrollUserService = inject(EnrollUserService);
  private indexedDBService = inject(IndexedDBService);
  private tokenService = inject(TokenService);
  private i18nService = inject(I18nService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public hasToLoginAuto = signal<UserToLogin | null>(null);
  public loadContinueAs = signal<any | null>(null);

  /**
   * Revisa los query params al cargar la app y ejecuta las acciones necesarias.
   */
  public handleQueryParams(): void {
    this.route.queryParams.pipe(
    ).subscribe(params => {
      const urlRedirect = params['url_redirect'];
      const userData = params['user'];
      const whatsAppUserData = params['user_token'];
      const courseToInscription = params['curso_id'];
      // params cuando user es invitado a grupo/capacitación
      const groupInvitedID = params['group_id'];
      const invitedUserID = params['user_id'];
      const invitedUserEmail = params['email'];
      const origin = params['origin'];

      // Si viene el param origin, lo guardamos en cookie compartida entre subdominios y aplicamos idioma
      if (origin) {
        const expire = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 1 año
        this.tokenService.saveToken('edutin_origin', origin, expire);
        this.i18nService.applyLanguageFromOrigin(origin);
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { origin: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      } else {
        // Sin query param, inicializamos idioma desde cookie existente
        //https://accounts.edutin.com/login?group_id=7ea5c6b4-6500-4c17-90dd-ec59b4e8b31f
        // 127.0.0.1:4200/login?group_id=7ea5c6b4-6500-4c17-90dd-ec59b4e8b31f
        // https://dev-app-local.com:4200/login?group_id=7ea5c6b4-6500-4c17-90dd-ec59b4e8b31f
        // https://dev-app-local.com:8100/user/login?group_id=7ea5c6b4-6500-4c17-90dd-ec59b4e8b31f
        this.i18nService.initializeLanguageFromOriginCookie();
      }

      // Si viene una URL de redirección, la guardamos para usarla después del login.
      if (urlRedirect) {
        this.handleUrlRedirect(urlRedirect, userData);
        return;
      }

      // Si hay sesión activa y una URL guardada, redirigimos inmediatamente.
      if (this.tokenService.isTokenValid() && this.getStorageItem('url_redirect')) {
        // this.redirectAfterLogin();
        return;
      }

      if (courseToInscription) {
        //Si el param viene con curso_id, es para inscribirse en un curso después de autenticarse
        this.hasToEnrollUserToCourse(courseToInscription);
        return;
      }


      // Si viene con invitación de unirse a grupo/capacitación

      if (groupInvitedID) {
        this.handleGroupInvitation(groupInvitedID, invitedUserID, invitedUserEmail);
        return;
      }
      // this.autoLogin(userData);// Si vienen datos de usuario, intentamos el login automático.
      // window.location.href = environment.mainApp;
    });
  }

  private autoLogin(userData: any) {
    // Si vienen datos de usuario, intentamos el login automático.
    if (userData) {
      const CREDENTIALS: UserToLogin = {
        username: userData.username || userData.email, //Email or phone_number
        password: userData.password,
        token: window.btoa(userData.password),
        fuente: userData?.fuente ?? 'form',
      };

      this.hasToLoginAuto.set(CREDENTIALS);
    }
  }


  providers = ['apple', 'google', 'facebook']
  handleUrlRedirect(urlRedirect: string, user: any) {
    const decodedUrl = decodeURIComponent(urlRedirect);
    this.setStorageItem('url_redirect', decodedUrl);

    if (user) {
      const userDecode = JSON.parse(window.atob(user));
      const requiredPass = !this.providers.includes(userDecode?.fuente);
      if (requiredPass) userDecode['password'] = userDecode?.password || 'required';
      userDecode['username'] = userDecode?.email || userDecode?.username || '';

      if (this.isAuthenticatedThisUser(userDecode?.id)) {
        this.redirectAfterLogin();
        return;
      }

      this.loadContinueAs.set(userDecode);
    }
  }

  isAuthenticatedThisUser(userID: string): boolean {
    if (!this.tokenService.isTokenValid()) return false;
    //Si hay cuenta autenticada
    const { id } = this.tokenService.getDecodedToken();
    if (id == userID) return true;
    return false;
  }

  async handleGroupInvitation(groupID: string, userID: string, UserEmail: string) {
    const groupInvitationData = JSON.stringify({ group_id: groupID, user_id: userID, isNew: true });
    const urlWithQParam = `${environment.mainApp}/home?groupInvitation=${groupInvitationData}`;

    if (this.tokenService.isTokenValid()) {
      if (userID) {
        const { id } = this.tokenService.getDecodedToken();
        if (id == userID) {
          window.location.href = urlWithQParam;
          return;
        }
      } else {
        // Sin user_id pero con sesión activa: redirigir directo con la data del grupo
        window.location.href = urlWithQParam;
        return;
      }

      if (UserEmail) this.hasEmailToAuth(UserEmail);
    }

    // Sin sesión: guardar url_redirect para redirigir tras login o signup
    this.setStorageItem('url_redirect', urlWithQParam);

    if (!userID) return;

    const userExists = await this.indexedDBService.getUser(userID);
    if (userExists) {
      this.autoLogin(userExists);
      return;
    }

    if (UserEmail) this.hasEmailToAuth(UserEmail);
  }


  hasEmailToAuth(UserEmail: string) {
    //Si trae email es porque se acaba de crear la cuenta desde business al crear  la invitación a grupo y tiene pass por defecto
    this.tokenService.clearCookie('accessToken');
    this.tokenService.clearCookie('refreshToken');

    const userCred = { email: UserEmail, password: 'edutin123' };
    this.autoLogin(userCred);
  }


  /**
   * Se ejecuta justo después de haber iniciado sesión, para verificar
   * Si tiene alguna acción pendiente por realizar
   * @returns {boolean} Retorna `false` si se manejó una redirección o inscripción,
   * de lo contrario retorna `true` para indicar que se puede continuar con el flujo normal.
   */
  handleAccessToRedirect(): boolean {
    if (this.getStorageItem('url_redirect')) {
      // Si requiere redirigir a una ruta en especifico
      this.redirectAfterLogin();
      return false; // Se manejó una condición, no continuar.
    }

    if (this.getStorageItem('courseIDToEnroll')) {
      // Si requiere inscribir al usuario a un curso antes de redirigir
      const courseID = this.getStorageItem('courseIDToEnroll');
      this.enrollUserService.initEnroll(courseID);
      return false; // Se manejó una condición, no continuar.
    }

    // No se encontró ninguna condición, sí se puede continuar.
    return true;
  }


  /**
   *
   */
  async hasToEnrollUserToCourse(courseID: string) {
    this.setStorageItem('courseIDToEnroll', courseID);
    const users = await this.indexedDBService.getAllUsers();
    if (users?.length > 0) {
      this.router.navigate(['/accounts']);
    }
  }

  /**
   * Revisa si hay una URL guardada y redirige.
   * Si no, redirige a la página principal por defecto.
   */
  public redirectAfterLogin(): void {
    const key = 'url_redirect';
    const redirectUrl = this.getStorageItem(key)?.replaceAll("'", '');
    if (redirectUrl) {
      this.clearStorageItem(key); // ¡Limpiamos la URL para futuros inicios de sesión!
      window.location.href = redirectUrl; // Usar para URLs externas
    } else {
      // this.router.navigate(['/dashboard']); // Ruta por defecto post-login
    }
  }

  // --- Métodos de Gestión de LocalStorage ---

  /**
   * Guarda la URL de redirección en localStorage.
   * @param url La URL a guardar.
   */
  public setStorageItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  /**
   * Obtiene la URL de redirección desde localStorage.
   * @returns La URL guardada o null si no existe.
   */
  public getStorageItem(key: string): any {
    return sessionStorage.getItem(key);
  }

  /**
   * Elimina la URL de redirección del localStorage.
   */
  public clearStorageItem(key: string): void {
    sessionStorage.removeItem(key);
  }
}
